import { PATH_DASHBOARD } from 'src/routes/paths';
import { getDiscriminators } from 'src/utils/permissions';
import { isOpsPlan, hasPlanCapability } from 'careit';

// Business/Donor Tours
import { dashboardHomeBusinessTour } from './steps/dashboardHomeBusiness';
import { donationsCreateTour } from './steps/donationsCreate';
import { donationsListTour } from './steps/donationsList';
import { donationsListFoodMoverTour } from './steps/donationsListFoodMover';
import { postDonationGuideTour } from './steps/postDonationGuide';
import { donationDetailTour } from './steps/donationDetail';
import { careitIdTour } from './steps/careitId';
import { settingsBusinessTour } from './steps/settingsBusiness';
import { partnershipsTour } from './steps/partnerships';
import { accountSettingsNpoTour } from './steps/accountSettingsNpo';
import { donationDetailNpoFreeTour } from './steps/donationDetailNpoFree';
import { scheduleCalendarBusinessFreeTour } from './steps/scheduleCalendarBusinessFree';

// NPO/Recipient Tours
import { dashboardHomeNpoTour } from './steps/dashboardHomeNpo';
import { findDonationsTour } from './steps/findDonations';
import { findDonationsFoodMoverTour } from './steps/findDonationsFoodMover';
import { rescuesListTour } from './steps/rescuesList';
import { rescuesListFoodMoverTour } from './steps/rescuesListFoodMover';
import { donorsListTour } from './steps/donorsList';
import { myTeamBusinessFreeTour } from './steps/myTeamBusinessFree';
import { myTeamNpoTour } from './steps/myTeamNpo';
import { myRescuesNpoTour } from './steps/myRescuesNpo';
import { rescueDetailNpoTour } from './steps/rescueDetailNpo';
import { scheduleCalendarFoodMoverTour } from './steps/scheduleCalendarFoodMover';
import { scheduleCalendarNpoTour } from './steps/scheduleCalendarNpo';
import { settingsNpoTour } from './steps/settingsNpo';

// Food Mover Tours
import { foodRunsOverviewTour } from './steps/foodRunsOverview';

// Marketplace Tours
import { marketplaceBrowseTour } from './steps/marketplaceBrowse';
import { marketplaceMyListingsTour } from './steps/marketplaceMyListings';
import { marketplaceListingEditorTour } from './steps/marketplaceListingEditor';
import { marketplaceIncomingOrdersTour } from './steps/marketplaceIncomingOrders';

// All tour configurations
export const tourConfigs = {
  // Business/Donor
  dashboard_home_business: dashboardHomeBusinessTour,
  donations_create: donationsCreateTour,
  donations_list: donationsListTour,
  donations_list_food_mover: donationsListFoodMoverTour,
  post_donation_guide: postDonationGuideTour,
  donation_detail: donationDetailTour,
  donation_detail_npo_free: donationDetailNpoFreeTour,
  careit_id: careitIdTour,
  account_settings_npo: accountSettingsNpoTour,
  settings_business: settingsBusinessTour,
  partnerships: partnershipsTour,
  schedule_calendar_business_free: scheduleCalendarBusinessFreeTour,

  // NPO/Recipient
  dashboard_home_npo: dashboardHomeNpoTour,
  find_donations: findDonationsTour,
  find_donations_food_mover: findDonationsFoodMoverTour,
  rescues_list: rescuesListTour,
  rescues_list_food_mover: rescuesListFoodMoverTour,
  donors_list: donorsListTour,
  my_team_business_free: myTeamBusinessFreeTour,
  my_team_npo: myTeamNpoTour,
  my_rescues_npo: myRescuesNpoTour,
  rescue_detail_npo: rescueDetailNpoTour,
  schedule_calendar_food_mover: scheduleCalendarFoodMoverTour,
  schedule_calendar_npo: scheduleCalendarNpoTour,
  settings_npo: settingsNpoTour,

  // Food Mover
  food_runs_overview: foodRunsOverviewTour,

  // Marketplace
  marketplace_browse: marketplaceBrowseTour,
  marketplace_my_listings: marketplaceMyListingsTour,
  marketplace_listing_editor: marketplaceListingEditorTour,
  marketplace_incoming_orders: marketplaceIncomingOrdersTour,
};

// Path to tour key mapping
const pathToTourMap = {
  // Business paths
  [PATH_DASHBOARD.root]: (user) => {
    const disc = getDiscriminators(user);
    return disc.orgType === 'business'
      ? 'dashboard_home_business'
      : 'dashboard_home_npo';
  },
  [PATH_DASHBOARD.donations]: (user) => {
    const disc = getDiscriminators(user);
    return disc.orgType === 'npo' && isOpsPlan(disc.plan)
      ? 'donations_list_food_mover'
      : 'donations_list';
  },
  [PATH_DASHBOARD.findDonations]: (user) => {
    const disc = getDiscriminators(user);
    return isOpsPlan(disc.plan)
      ? 'find_donations_food_mover'
      : 'find_donations';
  },
  [PATH_DASHBOARD.findItems.donations]: (user) => {
    const disc = getDiscriminators(user);
    return hasPlanCapability(disc.plan, 'proOps')
      ? 'find_donations_food_mover'
      : 'find_donations';
  },
  [PATH_DASHBOARD.myrescues]: 'my_rescues_npo',
  [PATH_DASHBOARD.rescues]: (user) => {
    const disc = getDiscriminators(user);
    return disc.orgType === 'npo' && isOpsPlan(disc.plan)
      ? 'rescues_list_food_mover'
      : 'rescues_list';
  },
  [PATH_DASHBOARD.careitId]: 'careit_id',
  [PATH_DASHBOARD.business]: 'donors_list',
  [PATH_DASHBOARD.teams]: (user) => {
    const disc = getDiscriminators(user);
    if (disc.orgType === 'business') return 'my_team_business_free';
    return disc.orgType === 'npo' ? 'my_team_npo' : null;
  },
  [PATH_DASHBOARD.schedule.root]: (user) => {
    const disc = getDiscriminators(user);
    if (disc.orgType === 'business') {
      return 'schedule_calendar_business_free';
    }
    if (disc.orgType !== 'npo') return null;
    return isOpsPlan(disc.plan)
      ? 'schedule_calendar_food_mover'
      : 'schedule_calendar_npo';
  },
  [PATH_DASHBOARD.partnerships]: 'partnerships',
  [PATH_DASHBOARD.foodRun?.allRuns]: 'food_runs_overview',

  // Marketplace — browsing is open to every buyer org; the seller pages are
  // gated to orgs with the marketplace seller flag enabled.
  [PATH_DASHBOARD.findItems.marketplace]: 'marketplace_browse',
  [PATH_DASHBOARD.marketplace.myListings]: (user) =>
    user?.marketplace_enabled ? 'marketplace_my_listings' : null,
  [PATH_DASHBOARD.marketplace.createListing]: (user) =>
    user?.marketplace_enabled ? 'marketplace_listing_editor' : null,
  [PATH_DASHBOARD.marketplace.incomingOrders]: (user) =>
    user?.marketplace_enabled ? 'marketplace_incoming_orders' : null,
};

const getSettingsTourKey = (user) => {
  const disc = getDiscriminators(user);
  return disc.orgType === 'business' ? 'settings_business' : 'settings_npo';
};

const getAccountSettingsTourKey = (user) => {
  const disc = getDiscriminators(user);
  return disc.orgType === 'npo' ? 'account_settings_npo' : null;
};

export const getTourKeyForPath = (path, user) => {
  const exactMatch = pathToTourMap[path];
  if (exactMatch) {
    return typeof exactMatch === 'function' ? exactMatch(user) : exactMatch;
  }

  if (
    path?.startsWith(PATH_DASHBOARD.settings?.account?.('')) ||
    path?.startsWith('/dashboard/settings/account')
  ) {
    return getAccountSettingsTourKey(user);
  }

  if (
    path?.startsWith(PATH_DASHBOARD.settings?.business?.('')) ||
    path?.startsWith('/dashboard/settings')
  ) {
    return getSettingsTourKey(user);
  }

  if (path?.includes('/donation/new') || path?.includes('/donations/new')) {
    return 'donations_create';
  }

  // The listing editor's edit route carries a dynamic id
  // (/marketplace/listings/:id/edit); reuse the create tour there, gated to
  // seller-enabled orgs.
  if (/\/marketplace\/listings\/[^/]+\/edit$/.test(path || '')) {
    return user?.marketplace_enabled ? 'marketplace_listing_editor' : null;
  }

  const donationDetailMatch = path?.match(/^\/dashboard\/donations\/[^/]+$/);
  if (donationDetailMatch) {
    const disc = user ? getDiscriminators(user) : null;
    if (disc?.orgType === 'npo') {
      return disc.plan === 'free'
        ? 'donation_detail_npo_free'
        : 'rescue_detail_npo';
    }
    return 'donation_detail';
  }

  // Only treat `path` as under `tourPath` when the next character is a segment boundary.
  const isUnderTourPath = (p, base) => {
    if (!p?.startsWith(base)) return false;
    const rest = p.slice(base.length);
    return (
      rest === '' ||
      rest.startsWith('/') ||
      rest.startsWith('?') ||
      rest.startsWith('#')
    );
  };

  for (const [tourPath, tourKey] of Object.entries(pathToTourMap)) {
    if (
      tourPath &&
      tourPath !== PATH_DASHBOARD.root &&
      isUnderTourPath(path, tourPath)
    ) {
      return typeof tourKey === 'function' ? tourKey(user) : tourKey;
    }
  }

  return null;
};

export const getTourForPath = (path, user) => {
  if (user) {
    const disc = getDiscriminators(user);
    if (disc.userType === 'super-admin') {
      return null;
    }
  }

  const tourKey = getTourKeyForPath(path, user);
  if (!tourKey) return null;

  const config = tourConfigs[tourKey];
  if (!config) return null;

  if (config.conditions && user) {
    const disc = getDiscriminators(user);
    const { orgTypes, userTypes, plans } = config.conditions;

    if (orgTypes && !orgTypes.includes(disc.orgType)) {
      return null;
    }
    if (userTypes && !userTypes.includes(disc.userType)) {
      return null;
    }
    if (plans && !plans.includes(disc.plan)) {
      return null;
    }
  }

  return config;
};

export const getTourByKey = (tourKey) => {
  return tourConfigs[tourKey] || null;
};
